/**
 * 個人レポート v2 の文言引き当て。
 *
 * 正典: apps/web/docs/新方針20260821/GB人財_個人レポート文言DB_v4_3.json
 *       （src/features/analysis/data/personalReportTextsV4_3.json に複製）
 *
 * 【DB化について】
 *  最終的には judgment_patterns と同様にDBへ格納する予定。まずは画面を固めるため
 *  JSONを直接読んでいる。DB化するときは loadPersonalTexts() の中身を
 *  API取得に差し替えるだけで済むよう、参照側は必ずこのモジュール経由にする。
 *  （全17ブロック246件と小さいので、起動時に全件持つ想定）
 *
 * 【ID規則】文言DBのIDは機械的に組み立てられる。
 *   PA01/02/03 … `01-{優勢軸連結}-{最低軸}` / 優勢軸ゼロなら `01-全方`
 *   PA04       … `04-{最低軸}`
 *   PA05       … `05-{役割}-{A|B|C|D}`（点数帯）
 *   PA09       … `09-{優勢軸連結}`
 *   PA10       … `10-{役割}-{高|低}`
 *   PA15       … `15-{まとまり}-{高|低}`
 */
import raw from '../data/personalReportTextsV4_3.json';
import type { AxisKey } from './gb74Report';
import type { Gb74ReportV2, RoleName, TraitKey } from './gb74ReportV2';

/** 文言DBの1エントリ。 */
export interface TextEntry {
  id: string;
  条件: string;
  見出し?: string;
  本文: string;
  裏面の出どころ?: string;
  形状?: string;
}

interface TextBlock {
  name: string;
  判定入力: string;
  出力: string;
  注記: unknown;
  entries: TextEntry[];
}

type TextDb = Record<string, TextBlock>;

const DB = raw as unknown as TextDb;

/**
 * 文言DB全体を取得する。
 * DB化したらここを API 取得（＋キャッシュ）に差し替える。参照側は変更不要。
 */
export function loadPersonalTexts(): TextDb {
  return DB;
}

/** ブロックとIDを指定して1件引く。無ければ undefined。 */
export function findText(block: string, id: string): TextEntry | undefined {
  return DB[block]?.entries.find((e) => e.id === id);
}

/** 優勢軸の連結（例: ['D','C'] → 'DC'）。優勢軸ゼロなら null。 */
function domKey(dominant: AxisKey[]): string | null {
  return dominant.length > 0 ? dominant.join('') : null;
}

/**
 * PA01/PA02/PA03 のように「優勢軸 × 最低軸」で引くブロック。
 * 優勢軸ゼロのときは `{prefix}-全方` にフォールバックする。
 *
 * 注意: 文言DBのIDは優勢軸を **昇順**（AB / AC / BCD …）で持つため、
 * 表示用の降順コード（DC など）ではなく昇順に並べ替えて引く。
 */
function findByDomLow(
  block: string,
  prefix: string,
  dominant: AxisKey[],
  lowest: AxisKey
): TextEntry | undefined {
  const key = domKey(dominant);
  if (!key) return findText(block, `${prefix}-全方`);
  const sorted = [...dominant].sort().join('');
  // 3軸優勢のときは残る1軸が必ず最低軸になる（合計24の制約）
  return (
    findText(block, `${prefix}-${sorted}-${lowest}`) ??
    findText(block, `${prefix}-${sorted}`) ??
    findText(block, `${prefix}-全方`)
  );
}

/** PA05 の点数帯（70〜=A / 50〜=B / 30〜=C / 0〜=D）。 */
function scoreBand(score: number): 'A' | 'B' | 'C' | 'D' {
  if (score >= 70) return 'A';
  if (score >= 50) return 'B';
  if (score >= 30) return 'C';
  return 'D';
}

/**
 * 補い方(PA04)の本人向け文言。
 *
 * DBのPA04は上司・経営者向けの書き方（「○○さんには〜てもらうと」）で書かれている。
 * 本人向けレポートでは主語が変わるため、ここで最低軸ごとの本人向け表現を持つ。
 * 機械的な語尾置換は「書き出してもらう→書き出しる」のような崩れが出るため採らない。
 * DBの内容（何をすると何が良くなるか）はそのまま踏襲している。
 */
const COMPLEMENT_SELF: Record<AxisKey, string> = {
  A: '決める前に、判断の基準を3つだけ書き出してみてください。迷いが減ります。',
  B: '始める前に、締切とチェック項目を書き出してみてください。抜けがかなり減ります。',
  C: '決めたあと、影響を受ける人にひと声かけてみてください。話が通りやすくなります。',
  D: '月に一つだけ、試すことを決めてみてください。それだけで動きが出ます。',
};

/** 最低軸に応じた「補い方」（本人向け）。 */
export function getComplementSelf(lowestAxis: AxisKey): string {
  return COMPLEMENT_SELF[lowestAxis];
}

/** レポート1件ぶんの文言をまとめて引く。 */
export interface PersonalTextsV2 {
  /** PA09 タイプの1行説明 */
  typeLead?: TextEntry;
  /** PA01 思考スタイル */
  thought?: TextEntry;
  /** PA02 行動スタイル */
  action?: TextEntry;
  /** PA15 持ち味の出方（最上位・最下位） */
  traitTop?: TextEntry;
  traitLow?: TextEntry;
  /** PA05 役割の段階文言（役割名→エントリ） */
  roleBands: Partial<Record<RoleName, TextEntry>>;
  /** PA10 根拠（上位1つ・最下位1つ） */
  roleReasonTop?: TextEntry;
  roleReasonLow?: TextEntry;
  /** PA03 気をつけるところ / PA04 補い方 */
  caution?: TextEntry;
  complement?: TextEntry;
  /** PA11 考え方と行動 / PA16 伸びしろ */
  face?: TextEntry;
  growth?: TextEntry;
}

/** 算出結果から、表示に必要な文言を一括で引き当てる。 */
export function pickPersonalTexts(r: Gb74ReportV2): PersonalTextsV2 {
  const traitId = (t: TraitKey, hl: '高' | '低') => `15-${t}-${hl}`;

  const roleBands: Partial<Record<RoleName, TextEntry>> = {};
  r.roles.forEach((role) => {
    const e = findText('PA05', `05-${role.name}-${scoreBand(role.score)}`);
    if (e) roleBands[role.name] = e;
  });

  return {
    typeLead: domKey(r.dominantAxes)
      ? findText('PA09', `09-${[...r.dominantAxes].sort().join('')}`)
      : findText('PA09', '09-全方'),
    thought: findByDomLow('PA01', '01', r.dominantAxes, r.lowestAxis),
    action: findByDomLow('PA02', '02', r.dominantAxes, r.lowestAxis),
    traitTop: findText('PA15', traitId(r.traitTop, '高')),
    traitLow: findText('PA15', traitId(r.traitLow, '低')),
    roleBands,
    roleReasonTop: findText('PA10', `10-${r.roleReasonTop}-高`),
    roleReasonLow: findText('PA10', `10-${r.roleReasonLow}-低`),
    caution: findByDomLow('PA03', '03', r.dominantAxes, r.lowestAxis),
    complement: findText('PA04', `04-${r.lowestAxis}`),
    face: undefined, // PA11: 条件文の形が他と異なるため、画面実装時に対応する
    growth: undefined, // PA16: 同上
  };
}
